import axios from 'axios';
import { Restaurant, User, Review } from '../types';
import { API_BASE_URL } from '../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Restaurant endpoints
export const restaurantApi = {
  getAll: async (): Promise<Restaurant[]> => {
    const response = await api.get('/api/restaurants/');
    return response.data;
  },

  getById: async (id: string): Promise<Restaurant> => {
    const response = await api.get(`/api/restaurants/${id}/`);
    return response.data;
  },

  getNearby: async (latitude: number, longitude: number, radius: number = 5000): Promise<Restaurant[]> => {
    const response = await api.get('/api/restaurants/nearby/', {
      params: { latitude, longitude, radius },
    });
    return response.data;
  },

  search: async (query: string): Promise<Restaurant[]> => {
    const response = await api.get('/api/restaurants/search/', {
      params: { q: query },
    });
    return response.data;
  },
};

// User endpoints
export const userApi = {
  getProfile: async (): Promise<User> => {
    const response = await api.get('/api/users/profile/');
    return response.data;
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.put('/api/users/profile/', data);
    return response.data;
  },

  getFavorites: async (): Promise<Restaurant[]> => {
    const response = await api.get('/api/auth/favorites/');
    return response.data;
  },

  addFavorite: async (restaurantId: string): Promise<void> => {
    await api.post('/api/auth/favorites/', { restaurant_id: restaurantId });
  },

  removeFavorite: async (restaurantId: string): Promise<void> => {
    await api.delete(`/api/auth/favorites/${restaurantId}/`);
  },
};

// Review endpoints
export const reviewApi = {
  getByRestaurant: async (restaurantId: string): Promise<Review[]> => {
    const response = await api.get(`/api/restaurants/${restaurantId}/reviews/`);
    return response.data;
  },

  create: async (restaurantId: string, data: { rating: number; comment: string }): Promise<Review> => {
    const response = await api.post(`/api/restaurants/${restaurantId}/reviews/`, data);
    return response.data;
  },

  update: async (reviewId: string, data: { rating: number; comment: string }): Promise<Review> => {
    const response = await api.put(`/api/reviews/${reviewId}/`, data);
    return response.data;
  },

  delete: async (reviewId: string): Promise<void> => {
    await api.delete(`/api/reviews/${reviewId}/`);
  },
};

// Authentication endpoints
export const authApi = {
  login: async (email: string, password: string): Promise<{ access: string; refresh: string; user: User }> => {
    const response = await api.post('/api/auth/login/', { email, password });
    return response.data;
  },

  register: async (data: { name: string; email: string; password: string }): Promise<{ access: string; refresh: string; user: User }> => {
    const response = await api.post('/api/auth/register/', data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/api/auth/logout/');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  },
};

export default api; 